import React, { useMemo, useState } from 'react';
import { Button, message, Table } from 'antd';
import type { TableColumnsType } from 'antd';
import { useLazyQuery, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import TablePaginationBar from '../../../../components/TablePaginationBar';
import PersonnelFilterArea, {
  DEFAULT_PERSONNEL_FILTERS,
  type PersonnelFilterOptions,
} from './components/PersonnelFilterArea';
import PersonnelAccountDrawer from './components/PersonnelAccountDrawer';
import {
  QUERY_OPDS_ACCOUNT_BY_PAGE,
  type OpdsAccountType,
  type QueryOpdsAccountByPageResponse,
  type QueryOpdsAccountByPageVariables,
} from './graphql/queries/queryOpdsAccountByPage';
import { GET_OPDS_ACCOUNT_DETAIL_BY_ID } from './graphql/queries/getOpdsAccountDetailById';
import { getPlatformPermissionErrorMessage } from '../utils/errorCode';
import { HasResource } from '../../../../components/HasResource';
import { ResourceCode } from 'constants/resourceCodes';

const PersonnelPage: React.FC = () => {
  const { t } = useTranslation();
  /** 表单中的条件（改输入/下拉不发起列表查询） */
  const [draftFilters, setDraftFilters] = useState<PersonnelFilterOptions>(DEFAULT_PERSONNEL_FILTERS);
  /** 点击「筛选」后用于列表接口的条件 */
  const [appliedFilters, setAppliedFilters] = useState<PersonnelFilterOptions>(DEFAULT_PERSONNEL_FILTERS);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>('create');
  const [editDetail, setEditDetail] = useState<OpdsAccountType | null>(null);

  const variables: QueryOpdsAccountByPageVariables = useMemo(() => {
    const idTrim = appliedFilters.id.trim();
    const idNum = idTrim ? Number(idTrim) : NaN;
    const idCondition = Number.isFinite(idNum) && idNum > 0 ? idNum : undefined;
    return {
      condition: {
        id: idCondition ?? undefined,
        name: appliedFilters.name.trim() || undefined,
        phone: appliedFilters.phone.trim() || undefined,
        status: appliedFilters.status === 2 ? undefined : appliedFilters.status,
      },
      pageRequest: {
        page: currentPage,
        size: pageSize,
        sortDirection: 'DESC',
      },
    };
  }, [appliedFilters, currentPage, pageSize]);

  const { data, loading, refetch } = useQuery<QueryOpdsAccountByPageResponse, QueryOpdsAccountByPageVariables>(
    QUERY_OPDS_ACCOUNT_BY_PAGE,
    {
      variables,
      fetchPolicy: 'cache-and-network',
    }
  );

  const [fetchDetail, { loading: detailLoading }] = useLazyQuery(GET_OPDS_ACCOUNT_DETAIL_BY_ID, {
    fetchPolicy: 'network-only',
  });

  const [loadingDetailId, setLoadingDetailId] = useState<number | null>(null);

  const pageData = data?.queryOpdsAccountByPage;

  const getStatusLabel = (status: number | null | undefined) => {
    if (status === 1) return String(t('central.platformPermission.personnel.filters.statusActive'));
    if (status === 0) return String(t('central.platformPermission.personnel.filters.statusInactive'));
    if (status === 2) return String(t('central.platformPermission.personnel.filters.statusAll'));
    return '-';
  };

  const formatPhone = (record: OpdsAccountType) => {
    if (record.phone == null && record.countryCode == null) return '-';
    const cc = record.countryCode != null ? `+${record.countryCode} ` : '';
    const num = record.phone != null ? String(record.phone) : '';
    return `${cc}${num}`.trim() || '-';
  };

  const columns: TableColumnsType<OpdsAccountType> = useMemo(
    () => [
      {
        title: 'ID',
        dataIndex: 'id',
        key: 'id',
        width: 100,
        render: (_: unknown, record: OpdsAccountType) => (
          <Button
            type="link"
            className="px-0"
            loading={detailLoading && loadingDetailId === Number(record.id)}
            onClick={() => void handleIdClick(Number(record.id))}
          >
            {record.id ?? '-'}
          </Button>
        ),
      },
      {
        title: t('central.personnel.columns.name'),
        dataIndex: 'name',
        key: 'name',
        width: 150,
        render: (v: string) => <span className="whitespace-nowrap">{v || '-'}</span>,
      },
      {
        title: t('central.personnel.columns.phone'),
        key: 'phone',
        width: 160,
        render: (_: unknown, record: OpdsAccountType) => (
          <span className="whitespace-nowrap">{formatPhone(record)}</span>
        ),
      },
      {
        title: t('central.personnel.columns.role'),
        dataIndex: 'roleNames',
        key: 'roleNames',
        width: 180,
        render: (v: string[] | null | undefined) => (
          <span className="whitespace-nowrap">{(v && v.length > 0 ? v.join(', ') : '-') || '-'}</span>
        ),
      },
      {
        title: t('central.personnel.columns.status'),
        dataIndex: 'status',
        key: 'status',
        width: 120,
        render: (status: number | null | undefined) => {
          const color = status === 1 ? 'text-green-600' : 'text-gray-400';
          return (
            <span className={`${color} whitespace-nowrap`}>{getStatusLabel(status ?? null)}</span>
          );
        },
      },
    ],
    [t, detailLoading, loadingDetailId]
  );

  const handleIdClick = async (id: number) => {
    setLoadingDetailId(id);
    try {
      const { data: detailData } = await fetchDetail({ variables: { id } });
      const d = detailData?.getOpdsAccountDetailById;
      if (!d?.success || !d.data) {
        const toastText = getPlatformPermissionErrorMessage(t, undefined, d?.message);
        if (toastText) message.error(toastText);
        return;
      }
      setEditDetail(d.data);
      setDrawerMode('edit');
      setDrawerVisible(true);
    } catch (e: any) {
      const toastText = getPlatformPermissionErrorMessage(t, undefined, e?.message);
      if (toastText) message.error(toastText);
    } finally {
      setLoadingDetailId(null);
    }
  };

  const showCreate = () => {
    setEditDetail(null);
    setDrawerMode('create');
    setDrawerVisible(true);
  };

  const handleApplyFilters = () => {
    setAppliedFilters({ ...draftFilters });
    setCurrentPage(1);
  };

  const handleResetFilters = () => {
    setDraftFilters(DEFAULT_PERSONNEL_FILTERS);
    setAppliedFilters(DEFAULT_PERSONNEL_FILTERS);
    setCurrentPage(1);
  };

  return (
    <div className="bg-white rounded-lg shadow-sm px-3 pb-3 pt-0">
      <PersonnelFilterArea filters={draftFilters} setFilters={setDraftFilters} />

      <div className="flex justify-between items-center gap-2 mb-3 pl-0">
        <HasResource resourceCodes={[ResourceCode.personnel.ADD]}>
          <Button type="primary" onClick={showCreate} className="w-auto min-w-24">
            <span className="font-bold">{t('central.personnel.filters.new')}</span>
          </Button>
        </HasResource>
        <div className="flex items-center gap-2">
          <Button
            type="primary"
            onClick={handleApplyFilters}
            className="w-auto min-w-24"
          >
            <span className="font-bold">{t('components.button.filter')}</span>
          </Button>
          <Button onClick={handleResetFilters} className="w-auto min-w-24">
            <span className="font-bold">{t('components.button.reset')}</span>
          </Button>
        </div>
      </div>

      <Table<OpdsAccountType>
        rowKey={(r) => String(r.id)}
        loading={loading}
        columns={columns}
        dataSource={pageData?.content ?? []}
        pagination={false}
        scroll={{ x: 'max-content' }}
        size="middle"
        tableLayout="fixed"
        className="mb-4"
      />

      <TablePaginationBar
        total={pageData?.totalElements ?? 0}
        current={currentPage}
        pageSize={pageSize}
        onChange={(page, size) => {
          setCurrentPage(page);
          setPageSize(size);
        }}
        onShowSizeChange={(_, size) => {
          setCurrentPage(1);
          setPageSize(size);
        }}
        pageSizeOptions={['10', '20', '50', '100']}
      />

      <PersonnelAccountDrawer
        visible={drawerVisible}
        mode={drawerMode}
        onClose={() => setDrawerVisible(false)}
        onSuccess={() => void refetch()}
        detail={editDetail}
      />
    </div>
  );
};

export default PersonnelPage;
